import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/** tailwind-merge con los tokens propios del tema (app/index.css): sin esto
 *  no sabe que `h-control` choca con `h-7` ni que `text-control` es un
 *  tamaño de texto (lo tomaría por color y lo descartaría). */
const twMerge = extendTailwindMerge({
  extend: {
    theme: { spacing: ["control", "control-inner"] },
    classGroups: { "font-size": [{ text: ["control"] }] },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function groupBy<T extends Record<string, any>, K extends keyof T>(
  array: T[],
  key: K,
): Record<string, T[]> {
  return array.reduce<Record<string, T[]>>(
    (acc, item) => {
      const group = String(item[key]);
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(item);
      return acc;
    },
    {} as Record<string, T[]>,
  );
}

/**
 * Configuración para transformar datos en matriz agrupada
 */
export interface MatrixTransformConfig<TInput, TOutput> {
  /** Función para generar la clave de agrupación */
  getGroupKey: (item: TInput) => string;
  /** Función para crear el objeto base del grupo */
  createGroupBase: (item: TInput) => TOutput;
  /** Función para actualizar el grupo con el item actual */
  updateGroup: (group: TOutput, item: TInput) => void;
}

/**
 * Transforma una lista de elementos en una matriz agrupada usando configuración personalizada
 * @param items - Array de elementos a transformar
 * @param config - Configuración de transformación
 * @returns Array de elementos agrupados
 */
export function transformToMatrix<TInput, TOutput>(
  items: TInput[],
  config: MatrixTransformConfig<TInput, TOutput>,
): TOutput[] {
  const groups = new Map<string, TOutput>();

  items.forEach((item) => {
    const key = config.getGroupKey(item);

    if (!groups.has(key)) {
      groups.set(key, config.createGroupBase(item));
    }

    const group = groups.get(key)!;
    config.updateGroup(group, item);
  });

  return Array.from(groups.values());
}

/**
 * Utilities para manejar múltiples rangos numéricos en URL params
 * Formato: numericRanges=total_100_200_subtotal_50_150_quantity_10_50
 */

export interface NumericRangeFilter {
  id: string;
  min: number | undefined;
  max: number | undefined;
}

/**
 * Parsea el string de numericRanges a un objeto estructurado
 * @param numericRangesParam - String de URL param (ej: "total_100_200_subtotal_50_150")
 * @returns Array de filtros con id, min, max
 */
export function parseNumericRanges(
  numericRangesParam: string | null,
): NumericRangeFilter[] {
  if (!numericRangesParam || numericRangesParam.trim() === "") {
    return [];
  }

  const ranges: NumericRangeFilter[] = [];
  const parts = numericRangesParam.split("_");

  // Procesar en grupos de 3: id, min, max
  for (let i = 0; i < parts.length; i += 3) {
    if (i + 2 < parts.length) {
      const id = parts[i];
      const minStr = parts[i + 1];
      const maxStr = parts[i + 2];

      ranges.push({
        id,
        min: minStr === "null" || minStr === "" ? undefined : Number(minStr),
        max: maxStr === "null" || maxStr === "" ? undefined : Number(maxStr),
      });
    }
  }

  return ranges;
}

/**
 * Convierte un array de rangos a string de URL param
 * @param ranges - Array de filtros con id, min, max
 * @returns String para URL param (ej: "total_100_200_subtotal_50_150")
 */
export function stringifyNumericRanges(ranges: NumericRangeFilter[]): string {
  if (ranges.length === 0) return "";

  return ranges
    .map((range) => {
      const min = range.min !== undefined ? range.min : "null";
      const max = range.max !== undefined ? range.max : "null";
      return `${range.id}_${min}_${max}`;
    })
    .join("_");
}

/**
 * Obtiene un rango específico por su id
 * @param numericRangesParam - String de URL param
 * @param filterId - ID del filtro a buscar
 * @returns Rango encontrado o undefined
 */
export function getNumericRange(
  numericRangesParam: string | null,
  filterId: string,
): NumericRangeFilter | undefined {
  const ranges = parseNumericRanges(numericRangesParam);
  return ranges.find((r) => r.id === filterId);
}

/**
 * Actualiza o agrega un rango numérico
 * @param numericRangesParam - String de URL param actual
 * @param filterId - ID del filtro
 * @param min - Valor mínimo
 * @param max - Valor máximo
 * @returns Nuevo string de URL param
 */
export function updateNumericRange(
  numericRangesParam: string | null,
  filterId: string,
  min: number | undefined,
  max: number | undefined,
): string {
  const ranges = parseNumericRanges(numericRangesParam);

  // Buscar si ya existe este filtro
  const existingIndex = ranges.findIndex((r) => r.id === filterId);

  // Si min y max son undefined, eliminar el filtro
  if (min === undefined && max === undefined) {
    if (existingIndex >= 0) {
      ranges.splice(existingIndex, 1);
    }
  } else {
    // Actualizar o agregar
    const newRange: NumericRangeFilter = { id: filterId, min, max };

    if (existingIndex >= 0) {
      ranges[existingIndex] = newRange;
    } else {
      ranges.push(newRange);
    }
  }

  return stringifyNumericRanges(ranges);
}

/**
 * Elimina un rango numérico específico
 * @param numericRangesParam - String de URL param actual
 * @param filterId - ID del filtro a eliminar
 * @returns Nuevo string de URL param
 */
export function removeNumericRange(
  numericRangesParam: string | null,
  filterId: string,
): string {
  const ranges = parseNumericRanges(numericRangesParam);
  const filtered = ranges.filter((r) => r.id !== filterId);
  return stringifyNumericRanges(filtered);
}

/**
 * Limpia todos los rangos numéricos
 * @returns String vacío
 */
export function clearNumericRanges(): string {
  return "";
}

/**
 * Hook para trabajar con numericRanges en componentes
 * @param searchParams - URLSearchParams de Next.js
 * @returns Funciones helper
 */
export function useNumericRangesHelpers(searchParams: URLSearchParams) {
  const numericRangesParam = searchParams.get("numericRanges");

  return {
    // Obtener todos los rangos
    getAllRanges: () => parseNumericRanges(numericRangesParam),

    // Obtener un rango específico
    getRange: (filterId: string) =>
      getNumericRange(numericRangesParam, filterId),

    // Actualizar un rango
    updateRange: (
      filterId: string,
      min: number | undefined,
      max: number | undefined,
    ) => updateNumericRange(numericRangesParam, filterId, min, max),

    // Eliminar un rango
    removeRange: (filterId: string) =>
      removeNumericRange(numericRangesParam, filterId),

    // Limpiar todos
    clearRanges: () => clearNumericRanges(),

    // Verificar si un filtro tiene valores
    hasRange: (filterId: string) => {
      const range = getNumericRange(numericRangesParam, filterId);
      return (
        range !== undefined &&
        (range.min !== undefined || range.max !== undefined)
      );
    },
  };
}

// En un archivo utils/errors.ts
export function getErrorMessage(error: unknown): string {
  let message = "";

  // ConvexError("texto"): el mensaje para el usuario viene limpio en `data`.
  if (
    error &&
    typeof error === "object" &&
    "data" in error &&
    typeof (error as { data: unknown }).data === "string"
  ) {
    return (error as { data: string }).data;
  }

  if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === "string") {
    message = error;
  } else if (error && typeof error === "object" && "message" in error) {
    message = String((error as any).message);
  } else {
    message = "Unknown error";
  }

  const errorPattern = /Uncaught Error:\s*(.+?)\s+at\s/i;
  const match = message.match(errorPattern);

  if (match && match[1]) {
    message = match[1].trim();
  } else {
    message = message.replace(/\[CONVEX[^\]]*\]\s*/g, "");
    message = message.replace(/\[Request\s*ID[^\]]*\]\s*/g, "");
    message = message.replace(/\[Error\s*ID[^\]]*\]\s*/g, "");
    message = message.replace(/\[Function[^\]]*\]\s*/g, "");
    message = message.replace(/Server\s*Error\s*/gi, "");
    message = message.replace(/Uncaught\s*Error:\s*/gi, "");
    message = message.replace(/Error:\s*/gi, "");
    message = message.replace(/\s*Called\s*by\s*client\s*$/gi, "");
    message = message.replace(/\s*at\s+client\s*$/gi, "");

    const stackStart = message.search(/\n\s+at\s|\n\s*at\s|\sat\s/);
    if (stackStart !== -1) {
      message = message.substring(0, stackStart);
    }

    message = message.replace(/\s+/g, " ").trim();
  }

  if (!message) {
    message = "Ha ocurrido un error. Por favor, inténtalo de nuevo.";
  }

  return message;
}

/**
 * Genera un código personalizado para items custom
 * Formato: CUST-AÑO-SUFFIX
 * Ejemplo: CUST-2026-A1B2C3
 * @returns Código personalizado único
 */
export function generateCustomCode(): string {
  const currentYear = new Date().getFullYear();

  // Generar un sufijo aleatorio de 4-6 caracteres alfanuméricos
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const suffixLength = 4 + Math.floor(Math.random() * 3); // 4-6 caracteres
  let suffix = "";

  for (let i = 0; i < suffixLength; i++) {
    suffix += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  return `CUST-${currentYear}-${suffix}`;
}
